import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip } from 'recharts';
import { DashboardCarousel } from '../styles/standardStyles';
import NoDataPlaceholder from './NoDataPlaceholder';
// The Item-Wise Sales Report percentage is defined in one place and read from there by
// both this slide and the Stock Report card it links to, so the two always agree.
import { getItemMarginDisplay } from '../utils/itemWiseSalesMargin';
// "View Table" opens the Stock Report's own Item-Wise Sales Report modal — the same
// popup shell and the same per-item rows, so the report reads the same from either screen.
import ItemWiseSalesTableModal from './ItemWiseSalesTableModal';
import { useTranslate } from '../translations/useTranslate';

const GRAPH_TITLES = [
  'Top 5 Items by Gross Profit',
  'Gross Profit vs Manufacturing Cost',
  'Item-wise Sales Report',
  'Raw Material Stock Report',
  'Estimated vs Real-Time Usage'
];

const PERIOD_OPTIONS = ['today', 'week', 'month'];

const EMPTY_CAROUSEL_DATA = {
  topProfitItems: [],
  summary: {},
  itemwiseSales: [],
  rawMaterialStock: [],
  estimatedVsActual: []
};

const PERIOD_LABELS = {
  today: 'Today',
  week: 'Week',
  month: 'Month'
};

const DropdownControl = ({
  value,
  options,
  isOpen,
  disabled = false,
  onToggle,
  onSelect
}) => {
  const selectedLabel = PERIOD_LABELS[value] || PERIOD_LABELS.week;

  return (
    <div
      className="carousel-filter-dropdown-wrapper"
      style={{ position: 'relative' }}
      onPointerDown={(event) => event.stopPropagation()}
      onMouseDown={(event) => event.stopPropagation()}
      onTouchStart={(event) => event.stopPropagation()}
    >
      <button
        type="button"
        className="carousel-period-selector"
        onClick={(event) => {
          event.stopPropagation();
          if (!disabled) {
            onToggle();
          }
        }}
        disabled={disabled}
        aria-haspopup={disabled ? undefined : 'listbox'}
        aria-expanded={disabled ? undefined : isOpen}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: '8px 12px',
          border: '1px solid #e0e0e0',
          borderRadius: '0.5rem',
          backgroundColor: '#fff',
          cursor: disabled ? 'default' : 'pointer',
          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
          minWidth: '110px'
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: '500', color: '#000', marginRight: '6px' }}>
          {selectedLabel}
        </span>
        {!disabled && (
          <svg
            aria-hidden="true"
            style={{
              marginLeft: 'auto',
              width: '14px',
              height: '14px',
              display: 'block'
            }}
            viewBox="0 0 20 20"
            fill="none"
          >
            <path d="M5 7.5L10 12.5L15 7.5" stroke="#000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>

      {!disabled && isOpen && (
        <div
          className="carousel-period-dropdown"
          role="listbox"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            right: 0,
            backgroundColor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: '0.5rem',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            zIndex: 120,
            overflow: 'hidden',
            minWidth: '110px'
          }}
        >
          {options.map((option) => {
            const isActive = value === option;

            return (
              <button
                type="button"
                key={option}
                role="option"
                aria-selected={isActive}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(option);
                }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  border: 'none',
                  background: isActive ? '#f5f5f5' : 'transparent',
                  padding: '10px 14px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: isActive ? '600' : '500',
                  color: '#000'
                }}
              >
                {PERIOD_LABELS[option] || option}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * The card menu on the Item-Wise Sales Report slide.
 *
 * The slide itself is a tap target that opens the full Stock Report, and the carousel
 * swipes, so every pointer event this control sees is stopped here — otherwise opening the
 * menu would either navigate away or drag the slide out from under the reader's finger.
 * Same reason DropdownControl above does it.
 */
const CardMenuControl = ({ isOpen, onToggle, actions }) => (
  <div
    className="carousel-card-menu-wrapper"
    style={{ position: 'relative', flex: '0 0 auto' }}
    onPointerDown={(event) => event.stopPropagation()}
    onMouseDown={(event) => event.stopPropagation()}
    onTouchStart={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
  >
    <button
      type="button"
      className="carousel-card-menu-button"
      onClick={(event) => {
        event.stopPropagation();
        onToggle();
      }}
      aria-haspopup="menu"
      aria-expanded={isOpen}
      aria-label="Report options"
      style={{
        width: '34px',
        height: '34px',
        borderRadius: '0.5rem',
        border: '1px solid #e0e0e0',
        backgroundColor: '#fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        padding: 0
      }}
    >
      <svg aria-hidden="true" viewBox="0 0 20 20" style={{ width: '16px', height: '16px' }} fill="none">
        <path d="M3 5.5h14M3 10h14M3 14.5h14" stroke="#000" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>

    {isOpen && (
      <div
        role="menu"
        style={{
          position: 'absolute',
          top: 'calc(100% + 4px)',
          right: 0,
          backgroundColor: '#fff',
          border: '1px solid #e0e0e0',
          borderRadius: '0.5rem',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 120,
          overflow: 'hidden',
          minWidth: '150px'
        }}
      >
        {actions.map((action) => (
          <button
            type="button"
            role="menuitem"
            key={action.label}
            onClick={(event) => {
              event.stopPropagation();
              action.onSelect();
            }}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 'none',
              background: 'transparent',
              fontSize: '13px',
              color: '#111827',
              cursor: 'pointer',
              whiteSpace: 'nowrap'
            }}
          >
            {action.label}
          </button>
        ))}
      </div>
    )}
  </div>
);

const formatUnitForDisplay = (unit) => {
  if (!unit) return '';

  const normalized = String(unit).toLowerCase().trim();
  if (normalized === 'kilogram' || normalized === 'kilograms') return 'kg';
  if (normalized === 'gram' || normalized === 'grams') return 'g';
  if (normalized === 'liter' || normalized === 'liters') return 'L';
  if (normalized === 'milliliter' || normalized === 'milliliters') return 'ml';

  return unit;
};

const roundToTwo = (value) => {
  const numeric = Number(value || 0);
  if (!Number.isFinite(numeric)) return 0;
  return Number(numeric.toFixed(2));
};

const autoConvertQuantity = (value, unit) => {
  if (!Number.isFinite(value)) {
    return { value: '--', unit };
  }

  const normalizedUnit = String(unit || '').toLowerCase().trim();

  // Scale on MAGNITUDE so negative stock scales like positive: |v| >= 1000 → high
  // unit, |v| < 1000 → base unit. Sign is preserved through the division.
  const mag = Math.abs(value);
  const round1 = (n) => (Math.abs(n) >= 10 ? Math.round(n) : Math.round(n * 10) / 10);

  if ((normalizedUnit === 'g' || normalizedUnit === 'gram' || normalizedUnit === 'grams') && mag >= 1000) {
    return { value: round1(value / 1000), unit: 'kg' };
  }

  if ((normalizedUnit === 'ml' || normalizedUnit === 'milliliter' || normalizedUnit === 'milliliters') && mag >= 1000) {
    return { value: round1(value / 1000), unit: 'L' };
  }

  if ((normalizedUnit === 'mg' || normalizedUnit === 'milligram' || normalizedUnit === 'milligrams') && mag >= 1000) {
    return { value: round1(value / 1000), unit: 'g' };
  }

  // Keep precision for small values (magnitude-based so negatives round the same)
  if (mag >= 100) return { value: Math.round(value), unit };
  if (mag >= 1) return { value: Math.round(value * 10) / 10, unit };
  return { value: parseFloat(value.toFixed(2)), unit };
};

const transformRawMaterialStock = (rawMaterial) => {
  if (!Array.isArray(rawMaterial)) {
    return [];
  }

  return rawMaterial.map((item) => {
    const hasSplitQuantities = Object.prototype.hasOwnProperty.call(item, 'before_qty')
      || Object.prototype.hasOwnProperty.call(item, 'after_qty');

    const beforeValue = hasSplitQuantities ? roundToTwo(item.before_qty) : roundToTwo(item.quantity);
    const wastedValue = hasSplitQuantities ? roundToTwo(item.wasted_qty) : 0;
    const deductedValue = hasSplitQuantities ? roundToTwo(item.deducted_qty) : null;
    const afterValue = hasSplitQuantities ? roundToTwo(item.after_qty) : roundToTwo(item.quantity);
    const unit = formatUnitForDisplay(item.unit || '');

    const beforeConverted = autoConvertQuantity(beforeValue, unit);
    const wastedConverted = autoConvertQuantity(wastedValue, unit);
    const deductedConverted = deductedValue === null
      ? { value: '-', unit }
      : autoConvertQuantity(deductedValue, unit);
    const afterConverted = autoConvertQuantity(afterValue, unit);

    const toLabel = (quantity) => (Number.isFinite(quantity) ? quantity : '--');

    return {
      name: item.name || item.ingredient_name || 'Unknown',
      before: `${toLabel(beforeConverted.value)} ${beforeConverted.unit}`.trim(),
      deducted: deductedConverted.value === '-'
        ? '-'
        : `${toLabel(deductedConverted.value)} ${deductedConverted.unit}`.trim(),
      wasted: `${toLabel(wastedConverted.value)} ${wastedConverted.unit}`.trim(),
      after: `${toLabel(afterConverted.value)} ${afterConverted.unit}`.trim(),
      status: item.stock_level || 'Sufficient'
    };
  });
};

const transformEstimatedUsage = (estimatedPayload) => {
  if (!estimatedPayload || !estimatedPayload.success || !Array.isArray(estimatedPayload.data)) {
    return [];
  }

  return estimatedPayload.data
    .map((item) => ({
      menu_item: item.menu_item || 'Unknown',
      estimated: Number(item.estimated || item.estimated_plates || 0) || 0,
      actual_plates: Number(item.actual_plates || 0) || 0,
      wasted_plates: Number(item.wasted_plates || 0) || 0,
      total_accounted: Number(item.total_accounted || 0) || 0
    }))
    .sort((a, b) => b.actual_plates - a.actual_plates);
};

const DashboardReportsCarousel = ({ goTo }) => {
  // The ingredient cost breakdown behind a row is fetched per language, the same way the
  // Stock Report fetches it.
  const { language } = useTranslate();
  const [carouselPeriod, setCarouselPeriod] = useState('week');
  const [openDropdownSlide, setOpenDropdownSlide] = useState(null);
  const [loadingCarousel, setLoadingCarousel] = useState(true);
  const [carouselData, setCarouselData] = useState(EMPTY_CAROUSEL_DATA);
  const [itemWiseMenuOpen, setItemWiseMenuOpen] = useState(false);
  const [showItemWiseTable, setShowItemWiseTable] = useState(false);

  useEffect(() => {
    if (openDropdownSlide === null) {
      return undefined;
    }

    const handleOutsidePress = (event) => {
      const target = event.target;
      const isInsideDropdown = target instanceof Element
        && Boolean(target.closest('.carousel-filter-dropdown-wrapper'));

      if (!isInsideDropdown) {
        setOpenDropdownSlide(null);
      }
    };

    document.addEventListener('mousedown', handleOutsidePress);
    document.addEventListener('touchstart', handleOutsidePress);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePress);
      document.removeEventListener('touchstart', handleOutsidePress);
    };
  }, [openDropdownSlide]);

  useEffect(() => {
    if (!itemWiseMenuOpen) {
      return undefined;
    }

    const handleOutsidePress = (event) => {
      const target = event.target;
      const isInsideMenu = target instanceof Element
        && Boolean(target.closest('.carousel-card-menu-wrapper'));

      if (!isInsideMenu) {
        setItemWiseMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsidePress);
    document.addEventListener('touchstart', handleOutsidePress);

    return () => {
      document.removeEventListener('mousedown', handleOutsidePress);
      document.removeEventListener('touchstart', handleOutsidePress);
    };
  }, [itemWiseMenuOpen]);

  useEffect(() => {
    const controller = new AbortController();
    let isMounted = true;

    const fetchCarouselData = async () => {
      try {
        setLoadingCarousel(true);

        const businessId = Number(localStorage.getItem('businessId'));
        if (!businessId) {
          console.error('No businessId found in localStorage');
          if (isMounted) {
            setCarouselData(EMPTY_CAROUSEL_DATA);
          }
          return;
        }

        const language = localStorage.getItem('selectedLanguage') || 'en';
        const apiUrl = process.env.REACT_APP_API_URL || 'https://be.invexis.co.in/api';

        const query = new URLSearchParams({
          businessId: String(businessId),
          language,
          period: carouselPeriod
        });

        const topProfitQuery = new URLSearchParams({
          businessId: String(businessId),
          language,
          period: carouselPeriod
        });

        const [topProfitRes, summaryRes, itemwiseRes, rawMaterialRes, estimatedRes] = await Promise.all([
          fetch(`${apiUrl}/reports/top-profit-items?${topProfitQuery.toString()}`, { signal: controller.signal }),
          fetch(`${apiUrl}/reports/summary?${query.toString()}`, { signal: controller.signal }),
          fetch(`${apiUrl}/stockrepo/item-wise-sales?${query.toString()}`, { signal: controller.signal }),
          fetch(`${apiUrl}/stockrepo/raw-material-stock?${query.toString()}`, { signal: controller.signal }),
          fetch(`${apiUrl}/stockrepo/estimated-vs-actual-plates?${query.toString()}`, { signal: controller.signal })
        ]);

        const [topProfit, summary, itemwise, rawMaterial, estimated] = await Promise.all([
          topProfitRes.ok ? topProfitRes.json() : [],
          summaryRes.ok ? summaryRes.json() : {},
          itemwiseRes.ok ? itemwiseRes.json() : [],
          rawMaterialRes.ok ? rawMaterialRes.json() : [],
          estimatedRes.ok ? estimatedRes.json() : { success: false, data: [] }
        ]);

        if (!isMounted) {
          return;
        }

        setCarouselData({
          topProfitItems: Array.isArray(topProfit) ? topProfit.slice(0, 5) : [],
          summary: summary || {},
          itemwiseSales: Array.isArray(itemwise) ? itemwise : [],
          rawMaterialStock: transformRawMaterialStock(rawMaterial),
          estimatedVsActual: transformEstimatedUsage(estimated)
        });
      } catch (error) {
        if (error?.name === 'AbortError') {
          return;
        }

        console.error('Error fetching dashboard carousel data:', error);
        if (isMounted) {
          setCarouselData(EMPTY_CAROUSEL_DATA);
        }
      } finally {
        if (isMounted) {
          setLoadingCarousel(false);
        }
      }
    };

    fetchCarouselData();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [carouselPeriod]);

  const handleSlideChange = useCallback((index) => {
    void index;
    setOpenDropdownSlide(null);
    setItemWiseMenuOpen(false);
  }, []);

  const toggleDropdown = useCallback((slideIndex) => {
    setOpenDropdownSlide((previous) => (previous === slideIndex ? null : slideIndex));
  }, []);

  const selectPeriod = useCallback((period) => {
    setCarouselPeriod(period);
    setOpenDropdownSlide(null);
  }, []);

  const usageChartOptions = useMemo(() => {
    const usageRows = carouselData.estimatedVsActual || [];

    return {
      chart: {
        type: 'column',
        backgroundColor: 'transparent',
        height: 240,
        spacing: [15, 5, 10, 5],
        marginLeft: 45,
        marginRight: 10,
        marginBottom: 90,
        marginTop: 20
      },
      title: { text: null },
      xAxis: {
        categories: usageRows.map((item) => item.menu_item || 'Unknown'),
        labels: {
          rotation: 0,
          align: 'center',
          style: {
            fontSize: '9px',
            color: '#374151',
            fontWeight: '500',
            width: '60px',
            textOverflow: 'none',
            whiteSpace: 'normal'
          },
          step: 1,
          useHTML: true,
          formatter: function formatter() {
            const text = String(this.value || '');
            const maxChars = 12;

            if (text.length <= maxChars) {
              return text;
            }

            const words = text.split(' ');
            let lineOne = '';
            let lineTwo = '';

            for (let index = 0; index < words.length; index += 1) {
              if (lineOne.length + words[index].length <= maxChars) {
                lineOne += `${lineOne ? ' ' : ''}${words[index]}`;
              } else {
                lineTwo += `${lineTwo ? ' ' : ''}${words[index]}`;
              }
            }

            return `${lineOne}${lineTwo ? `<br/>${lineTwo}` : ''}`;
          }
        }
      },
      yAxis: {
        min: 0,
        title: {
          text: 'Plates',
          style: { fontSize: '10px', color: '#6b7280' }
        },
        gridLineColor: '#e5e7eb',
        labels: { style: { fontSize: '9px', color: '#6b7280' } },
        allowDecimals: false,
        stackLabels: {
          enabled: true,
          style: {
            fontSize: '10px',
            fontWeight: 'bold',
            color: '#000',
            textOutline: '1px white'
          },
          verticalAlign: 'top',
          y: -5,
          formatter: function formatter() {
            return this.stack === 'actual' && this.total > 0 ? this.total : '';
          }
        }
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        borderWidth: 0,
        borderRadius: 0,
        shadow: false,
        padding: 0,
        style: {
          fontSize: '11px',
          color: '#111827',
          zIndex: 99999,
          pointerEvents: 'none'
        },
        formatter: function formatter() {
          const pointMap = {};
          (this.points || []).forEach((point) => {
            pointMap[point.series?.name] = Number(point.y || 0);
          });

          const itemName = usageRows[this.x]?.menu_item
            || this.points?.[0]?.key
            || this.points?.[0]?.category
            || this.key
            || String(this.x || '');

          const estimatedPlates = pointMap['Estimated Plates'] || 0;
          const actualPlates = pointMap['Actual Plates Sold'] || 0;
          const wastedPlates = pointMap['Wasted Plates'] || 0;

          return `
            <div style="background-color: #ffffff; padding: 8px 12px; border-radius: 8px; border: 1px solid #e5e7eb; box-shadow: 0 4px 14px rgba(0,0,0,0.12); font-family: inherit; min-width: 155px; box-sizing: border-box;">
              <div style="font-weight: 700; color: #111827; margin-bottom: 6px; font-size: 11px; border-bottom: 1px solid #f3f4f6; padding-bottom: 4px;">
                ${itemName}
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 4px; font-size: 10px; color: #374151;">
                <span><span style="color:#f59e0b; margin-right: 5px;">●</span>Estimated Plates:</span>
                <b style="color: #111827;">${estimatedPlates}</b>
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 4px; font-size: 10px; color: #374151;">
                <span><span style="color:#22c55e; margin-right: 5px;">●</span>Actual Sold:</span>
                <b style="color: #111827;">${actualPlates}</b>
              </div>
              <div style="display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-bottom: 5px; font-size: 10px; color: #374151;">
                <span><span style="color:#ef4444; margin-right: 5px;">●</span>Wasted Plates:</span>
                <b style="color: #111827;">${wastedPlates}</b>
              </div>
              <div style="border-top: 1px solid #e5e7eb; margin-top: 5px; padding-top: 5px; display: flex; align-items: center; justify-content: space-between; gap: 10px; font-size: 10px; color: #374151;">
                <span style="color: #4b5563;"><span style="color:#6b7280; margin-right: 5px;">●</span>Total Accounted:</span>
                <b style="color: #111827;">${actualPlates + wastedPlates}</b>
              </div>
            </div>
          `;
        }
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          pointPadding: 0.15,
          borderWidth: 0,
          borderRadius: 3,
          groupPadding: 0.1,
          dataLabels: { enabled: false }
        }
      },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: { fontSize: '10px', fontWeight: '500', color: '#374151' },
        y: 15
      },
      series: [
        {
          name: 'Estimated Plates',
          data: usageRows.map((item) => {
            const numeric = parseFloat(item.estimated || 0);
            return Number.isNaN(numeric) ? 0 : numeric;
          }),
          stack: 'estimated',
          color: '#f59e0b',
          dataLabels: {
            enabled: true,
            verticalAlign: 'top',
            y: -15,
            style: {
              fontSize: '9px',
              fontWeight: 'bold',
              textOutline: 'none',
              color: '#111827'
            },
            formatter: function formatter() {
              return this.y > 0 ? this.y : '';
            }
          }
        },
        {
          name: 'Wasted Plates',
          data: usageRows.map((item) => {
            const numeric = parseFloat(item.wasted_plates || 0);
            return Number.isNaN(numeric) ? 0 : numeric;
          }),
          stack: 'actual',
          color: '#ef4444',
          dataLabels: { enabled: false }
        },
        {
          name: 'Actual Plates Sold',
          data: usageRows.map((item) => {
            const numeric = parseFloat(item.actual_plates || 0);
            return Number.isNaN(numeric) ? 0 : numeric;
          }),
          stack: 'actual',
          color: '#22c55e',
          dataLabels: { enabled: false }
        }
      ],
      exporting: { enabled: false },
      credits: { enabled: false }
    };
  }, [carouselData.estimatedVsActual]);

  const carouselItems = useMemo(() => {
    const periodSelector = (slideIndex) => (
      <DropdownControl
        value={carouselPeriod}
        options={PERIOD_OPTIONS}
        isOpen={openDropdownSlide === slideIndex}
        onToggle={() => toggleDropdown(slideIndex)}
        onSelect={selectPeriod}
      />
    );

    return [
      {
        id: 'graph0',
        content: (
          <div className="inv-carousel-item-inner">
            <div className="inv-carousel-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <h3 className="inv-carousel-item-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {GRAPH_TITLES[0]}
                </h3>
              </div>
              {periodSelector(0)}
            </div>
            <div className="inv-carousel-item-body">
              {loadingCarousel ? (
                <div className="carousel-loading">Loading...</div>
              ) : (
                <div
                  className="graph-container"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sessionStorage.setItem('highlightSection', 'top-items');
                    goTo('sales-report');
                  }}
                >
                  {carouselData.topProfitItems && carouselData.topProfitItems.length > 0 ? (
                    <div className="graph-0-container">
                      {(() => {
                        // Mirrors SalesReport.js:4485-4513 so the two surfaces cannot disagree.
                        // A null gross_profit means "we cannot cost this", which is not the same
                        // as zero profit — coercing it to 0 would render a confident "0%".
                        const itemsWithPositiveProfit = carouselData.topProfitItems.map((item) => {
                          const isCogsUnavailable = !!(item.cogs_unavailable
                            || item.gross_profit === null || item.gross_profit === undefined);
                          return {
                            ...item,
                            _is_cogs_unavailable: isCogsUnavailable,
                            adjusted_gross_profit: isCogsUnavailable
                              ? null
                              : Math.max(0, Number(item.gross_profit || 0))
                          };
                        });

                        // Uncostable items are excluded from the denominator, so the remaining
                        // bars still add up to 100% of the profit we can actually account for.
                        const totalPositiveGrossProfit = itemsWithPositiveProfit
                          .filter((item) => item.adjusted_gross_profit !== null)
                          .reduce((sum, item) => sum + item.adjusted_gross_profit, 0);

                        return itemsWithPositiveProfit.map((item, index) => {
                          const isUnavailable = item.adjusted_gross_profit === null;
                          const profitPercentage = (!isUnavailable && totalPositiveGrossProfit > 0)
                            ? (item.adjusted_gross_profit / totalPositiveGrossProfit) * 100
                            : 0;
                          const displayPercentage = isUnavailable ? '-' : `${Math.round(profitPercentage)}%`;
                          const barWidthPercent = isUnavailable ? 15 : profitPercentage * 0.88;

                            return (
                                            <div key={index} className="graph-0-item" data-tooltip={item.item_name}>
                              <div className="graph-0-image">
                                <img
                                  src={item.image_url || 'https://via.placeholder.com/28'}
                                  alt={item.item_name}
                                  onError={(event) => {
                                    event.target.src = 'https://via.placeholder.com/28';
                                  }}
                                />
                              </div>
                              <div
                                className="graph-0-bar"
                                style={{
                                  width: `${Math.max(barWidthPercent, 15)}%`,
                                  // Tinted so an uncostable bar is not mistaken for a real result.
                                  ...(isUnavailable ? { backgroundColor: '#93b8f5' } : {})
                                }}
                              >
                                <span className="graph-0-bar-text">{item.item_name}</span>
                              </div>
                              <div
                                className="graph-0-percentage"
                                style={isUnavailable ? { color: '#666' } : undefined}
                                title={isUnavailable ? 'Gross profit unavailable: an ingredient has no valid cost basis' : undefined}
                              >{displayPercentage}</div>
                            </div>
                          );
                        });
                      })()}
                      <div className="graph-0-scale">
                        {[0, 20, 40, 60, 80, 100].map((tick, index) => (
                          <div key={index} className="graph-0-tick" style={{ left: `${tick}%` }}>
                            {tick === 0 ? '0' : `${tick}%`}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NoDataPlaceholder
                      type="profit"
                      title="No profit data yet"
                      subtitle="Your top earning items will rank here once sales are recorded."
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'graph1',
        content: (
          <div className="inv-carousel-item-inner">
            <div className="inv-carousel-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <h3 className="inv-carousel-item-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {GRAPH_TITLES[1]}
                </h3>
              </div>
              {periodSelector(1)}
            </div>
            <div className="inv-carousel-item-body">
              {loadingCarousel ? (
                <div className="carousel-loading">Loading...</div>
              ) : (
                <div
                  className="graph-container"
                  style={{ overflow: 'hidden', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                  onClick={() => {
                    sessionStorage.setItem('highlightSection', 'gross-profit');
                    goTo('sales-report');
                  }}
                >
                  {carouselData.summary && carouselData.summary.total_sales > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '0px', width: 'auto', padding: '0', boxSizing: 'border-box' }}>
                      <div style={{ flex: '0 0 145px', position: 'relative', marginLeft: '0' }}>
                        <ResponsiveContainer width="100%" height={160}>
                          <PieChart>
                            <Pie
                              data={(() => {
                                const totalWastage = Number(carouselData.summary.total_wastage || 0);
                                // Same donut, same defect as the Sales Report's: the green wedge
                                // read gross_profit (BEFORE wastage) while a Waste wedge was drawn
                                // beside it, so the wastage was counted twice and the slices
                                // overshot the Net Sale in the centre. Read the net figure — the
                                // one the Gross Profit card shows — so the three wedges sum to it.
                                const pieData = [
                                  { name: 'Gross Profit', value: Math.max(1, Number(
                                      (carouselData.summary.display_net_profit ?? carouselData.summary.net_profit
                                        ?? carouselData.summary.display_gross_profit ?? carouselData.summary.gross_profit) || 0
                                    )), color: '#34c759' },
                                  { name: 'Manufacturing Cost', value: Math.max(1, Number(carouselData.summary.total_cogs || 0)), color: '#FF9500' }
                                ];

                                if (totalWastage > 0) {
                                  pieData.push({ name: 'Waste', value: totalWastage, color: '#FF3B30' });
                                }

                                return pieData;
                              })()}
                              dataKey="value"
                              nameKey="name"
                              cx="50%"
                              cy="50%"
                              outerRadius={60}
                              innerRadius={40}
                              startAngle={90}
                              endAngle={450}
                              stroke="none"
                            >
                              {(() => {
                                const totalWastage = Number(carouselData.summary.total_wastage || 0);
                                return totalWastage > 0
                                  ? [
                                      <Cell key="profit" fill="#34c759" />,
                                      <Cell key="cogs" fill="#FF9500" />,
                                      <Cell key="waste" fill="#FF3B30" />
                                    ]
                                  : [
                                      <Cell key="profit" fill="#34c759" />,
                                      <Cell key="cogs" fill="#FF9500" />
                                    ];
                              })()}
                            </Pie>
                            <Tooltip
                              formatter={(value) => `₹${value.toLocaleString()}`}
                              contentStyle={{
                                fontSize: '12px',
                                padding: '6px 12px',
                                borderRadius: '8px',
                                backgroundColor: '#ffffff',
                                border: '1px solid #e5e7eb',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                color: '#111827'
                              }}
                              itemStyle={{ color: '#111827' }}
                              position={{ x: 0, y: -20 }}
                              wrapperStyle={{ zIndex: 1000 }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', textAlign: 'center', pointerEvents: 'none' }}>
                          <div style={{ fontSize: '9px', color: '#666', marginBottom: '2px', fontWeight: 500 }}>Net Sale</div>
                          <div style={{ fontSize: '13px', fontWeight: 700, color: '#333' }}>₹{(carouselData.summary.total_sales || 0).toLocaleString()}</div>
                        </div>
                      </div>

                      <div style={{ flex: '1', paddingTop: '0.5rem', paddingLeft: '0', paddingRight: '12px', minWidth: '0', textAlign: 'left', overflow: 'visible' }}>
                        {(() => {
                          const totalSales = Number(carouselData.summary.total_sales || 0);
                          // display_* carries the A2 subtotal — the same dishes the badge below
                          // says are excluded are already out of it. Reading the raw totals here
                          // would print a figure that includes revenue the badge claims to have
                          // removed, and this card would then disagree with the Sales Report
                          // about the same day.
                          // NET of wastage — the same chain the Sales Report's Gross Profit
                          // card reads. The wedge beside this legend draws the wastage as its
                          // own slice, so a gross figure here counted it twice and the legend
                          // disagreed with the card it is meant to mirror.
                          const grossProfit = Number(
                            (carouselData.summary.display_net_profit ?? carouselData.summary.net_profit
                              ?? carouselData.summary.display_gross_profit ?? carouselData.summary.gross_profit) || 0);
                          const totalWastage = Number(carouselData.summary.total_wastage || 0);
                          const cogs = Number(
                            (carouselData.summary.display_total_cogs ?? carouselData.summary.total_cogs) || 0);
                          // A2. Dash only when NOTHING on the day could be priced. Two unpriced
                          // dishes out of forty leave a real figure for the other thirty-eight,
                          // and blanking it hid a number the Sales Report rows already showed.
                          // The `??` fallback keeps the old (safe) behaviour for one cache TTL
                          // after deploy, before `all_cogs_unavailable` appears in responses.
                          // has_zero_cogs_items is the older, narrower flag; honoured so this
                          // card cannot disagree with the Sales Report about the same day.
                          const cogsUnavailable = !!(
                            (carouselData.summary.all_cogs_unavailable
                              ?? carouselData.summary.has_unavailable_cogs_items)
                            || carouselData.summary.has_zero_cogs_items);
                          const excludedCount = Number(carouselData.summary.excluded_cogs_item_count || 0);
                          const grossProfitPercent = totalSales > 0 ? (grossProfit / totalSales) * 100 : 0;
                          const cogsPercent = totalSales > 0 ? (cogs / totalSales) * 100 : 0;
                          const wastagePercent = totalSales > 0 ? (totalWastage / totalSales) * 100 : 0;
                          const showCogsDash = cogsUnavailable && totalSales > 0;
                          // Shown, but part of the cost behind it was priced from an earlier
                          // purchase price. Marked so this card cannot be mistaken for measured.
                          const estMark = (!cogsUnavailable && carouselData.summary.has_estimated_cogs_items) ? '~' : '';

                          return (
                            <>
                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
                                  <div style={{ width: '12px', height: '12px', backgroundColor: '#34c759', borderRadius: '3px', flexShrink: 0, marginTop: '3px' }}></div>
                                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left', overflow: 'visible' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '2px', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'visible' }}>
                                      Gross Profit {showCogsDash ? '-' : `${estMark}${grossProfitPercent.toFixed(1)}%`}
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: 700, color: showCogsDash ? '#666' : '#34c759', lineHeight: 1.2 }}>
                                      {showCogsDash ? '-' : `${estMark}₹${grossProfit.toLocaleString()}`}
                                    </div>
                                    {/* A2: the figure is a subtotal — say so, or it reads as complete. */}
                                    {!showCogsDash && excludedCount > 0 && (
                                      <div style={{ fontSize: '10px', color: '#6b7280', lineHeight: 1.3, marginTop: '1px' }}>
                                        excludes {excludedCount} unpriced item{excludedCount === 1 ? '' : 's'}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
                                  <div style={{ width: '12px', height: '12px', backgroundColor: '#FF9500', borderRadius: '3px', flexShrink: 0, marginTop: '3px' }}></div>
                                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left', overflow: 'visible' }}>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '2px', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'visible' }}>
                                      Manufacturing Cost {showCogsDash ? '' : `${estMark}${cogsPercent.toFixed(1)}%`}
                                    </div>
                                    <div style={{ fontSize: '14px', fontWeight: 700, color: '#FF9500', lineHeight: 1.2 }}>
                                      {showCogsDash ? '-' : `${estMark}₹${cogs.toLocaleString()}`}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {totalWastage > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '2px' }}>
                                    <div style={{ width: '12px', height: '12px', backgroundColor: '#FF3B30', borderRadius: '3px', flexShrink: 0, marginTop: '3px' }}></div>
                                    <div style={{ flex: 1, minWidth: 0, textAlign: 'left', overflow: 'visible' }}>
                                      <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '2px', lineHeight: 1.3, whiteSpace: 'nowrap', overflow: 'visible' }}>
                                        Waste {wastagePercent.toFixed(1)}%
                                      </div>
                                      <div style={{ fontSize: '14px', fontWeight: 700, color: '#FF3B30', lineHeight: 1.2 }}>
                                        ₹{totalWastage.toLocaleString()}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  ) : (
                    <NoDataPlaceholder
                      type="compare"
                      title="Nothing to compare yet"
                      subtitle="Add sales to see gross profit weighed against manufacturing cost."
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'graph2',
        content: (
          <div className="inv-carousel-item-inner">
            <div className="inv-carousel-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <h3 className="inv-carousel-item-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {GRAPH_TITLES[2]}
                </h3>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: '0 0 auto' }}>
                {periodSelector(2)}
              </div>
            </div>
            <div className="inv-carousel-item-body">
              {loadingCarousel ? (
                <div className="carousel-loading">Loading...</div>
              ) : (
                <div
                  className="graph-container"
                  style={{ width: '100%', cursor: 'pointer', touchAction: 'pan-y' }}
                  onClick={() => {
                    sessionStorage.setItem('selectedReportId', 'stock-itemwise');
                    sessionStorage.setItem('reportView', 'details');
                    goTo('reports');
                  }}
                >
                  {carouselData.itemwiseSales && carouselData.itemwiseSales.length > 0 ? (
                    <div style={{ width: '100%' }}>
                      <div
                        className="table-container itemwise-table-container"
                        style={{
                          width: '100%',
                          paddingRight: '2px',
                          overflowX: 'hidden',
                          WebkitOverflowScrolling: 'touch',
                          touchAction: 'pan-y'
                        }}
                      >
                        {carouselData.itemwiseSales.map((item, index) => (
                          <div
                            key={index}
                            className="graph-2-item"
                            style={{ borderBottom: index < carouselData.itemwiseSales.length - 1 ? '1px solid #e9ecef' : 'none' }}
                          >
                            <img src={item.image || 'https://placehold.co/40x40'} alt={item.name} className="graph-2-image" />
                            <div className="graph-2-details">
                              <h4 className="graph-2-name">{item.name}</h4>
                            </div>
                            <div className="graph-2-profit">
                              {/* The Stock Report's Item-Wise Sales Report figure, byte for byte.
                                  This slide used to print `contributionPercentage` — the item's
                                  share of period revenue — which is a different quantity: over
                                  Aug-2026 that showed Chicken Nuggets (Weekend) at 11.9% here and
                                  85.0% on the report this card opens. Same rows, same endpoint,
                                  now the same number. */}
                              {(() => {
                                const marginDisplay = getItemMarginDisplay(item);
                                return (
                                  <p
                                    className="graph-2-profit-value"
                                    style={{ color: marginDisplay.color }}
                                    title={marginDisplay.title}
                                  >
                                    {marginDisplay.text}
                                  </p>
                                );
                              })()}
                              <p className="graph-2-profit-change">{item.change || ''}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <NoDataPlaceholder
                      type="sales"
                      title="No item sales yet"
                      subtitle="A breakdown of sales by item appears here after your first sale."
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'graph3',
        content: (
          <div className="inv-carousel-item-inner">
            <div className="inv-carousel-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <h3 className="inv-carousel-item-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {GRAPH_TITLES[3]}
                </h3>
              </div>
              {periodSelector(3)}
            </div>
            <div className="inv-carousel-item-body">
              {loadingCarousel ? (
                <div className="carousel-loading">Loading...</div>
              ) : (
                <div
                  className="graph-container"
                  style={{ width: '100%', cursor: 'pointer', touchAction: 'pan-y' }}
                  onClick={() => {
                    sessionStorage.setItem('selectedReportId', 'stock-performance');
                    sessionStorage.setItem('reportView', 'details');
                    goTo('reports');
                  }}
                >
                  {carouselData.rawMaterialStock && carouselData.rawMaterialStock.length > 0 ? (
                    <div
                      className="table-container raw-material-stock-container"
                      style={{
                        width: '100%',
                        paddingRight: '2px',
                        overflowX: 'hidden',
                        WebkitOverflowScrolling: 'touch',
                        touchAction: 'pan-y'
                      }}
                    >
                      {carouselData.rawMaterialStock.map((item, index) => (
                        <div
                          key={index}
                          className="graph-3-item"
                          style={{ borderBottom: index < carouselData.rawMaterialStock.length - 1 ? '1px solid #e9ecef' : 'none' }}
                        >
                          <div className="graph-3-header">
                            <h4 className="graph-3-item-name">{item.name}</h4>
                            <span className={`graph-3-status ${item.status === 'Sufficient' ? 'sufficient' : item.status === 'Low' ? 'low' : 'critical'}`}>
                              {item.status}
                            </span>
                          </div>
                          <div className="graph-3-details">
                            <div className="graph-3-detail-item"><span className="graph-3-label">Before</span><span className="graph-3-value default">{item.before}</span></div>
                            <div className="graph-3-detail-item"><span className="graph-3-label">Deducted</span><span className="graph-3-value deducted">{item.deducted}</span></div>
                            <div className="graph-3-detail-item"><span className="graph-3-label">Wasted</span><span className="graph-3-value wasted">{item.wasted}</span></div>
                            <div className="graph-3-detail-item"><span className="graph-3-label">After</span><span className="graph-3-value default">{item.after}</span></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <NoDataPlaceholder
                      type="stock"
                      title="No stock data yet"
                      subtitle="Add stock-in items or inventory count to view raw material stock."
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      },
      {
        id: 'graph4',
        content: (
          <div className="inv-carousel-item-inner">
            <div className="inv-carousel-item-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                <h3 className="inv-carousel-item-title" style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {GRAPH_TITLES[4]}
                </h3>
              </div>
              {periodSelector(4)}
            </div>
            <div className="inv-carousel-item-body">
              {loadingCarousel ? (
                <div className="carousel-loading">Loading...</div>
              ) : (
                <div
                  className="graph-container"
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sessionStorage.setItem('selectedReportId', 'stock-consumption');
                    sessionStorage.setItem('reportView', 'details');
                    goTo('reports');
                  }}
                >
                  {carouselData.estimatedVsActual && carouselData.estimatedVsActual.length > 0 ? (
                    <div style={{ width: '100%', height: '240px', overflowX: 'auto' }}>
                      <div style={{ minWidth: `${Math.max(400, carouselData.estimatedVsActual.length * 60)}px`, height: '100%' }}>
                        <HighchartsReact highcharts={Highcharts} options={usageChartOptions} />
                      </div>
                    </div>
                  ) : (
                    <NoDataPlaceholder
                      type="usage"
                      title="No usage data yet"
                      subtitle="Compare expected vs actual consumption once items start moving."
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )
      }
    ];
  }, [carouselData, carouselPeriod, goTo, itemWiseMenuOpen, loadingCarousel, openDropdownSlide, selectPeriod, toggleDropdown, usageChartOptions]);

  return (
    <div className="card reports-carousel-card" style={{ padding: 0, overflow: 'hidden' }}>
      <DashboardCarousel
        // The carousel must not slide out from under an open menu or table.
        autoplay={openDropdownSlide === null && !itemWiseMenuOpen && !showItemWiseTable}
        autoplayDelay={10000}
        loop={true}
        initialIndex={3}
        onChange={handleSlideChange}
        items={carouselItems}
      />
      <ItemWiseSalesTableModal
        isOpen={showItemWiseTable}
        onClose={() => setShowItemWiseTable(false)}
        items={carouselData.itemwiseSales}
        language={language}
      />
    </div>
  );
};


export default DashboardReportsCarousel;
